import type {
  OrderStage,
  OrderPaymentStatus,
} from "../schemas/validators/enums.lib";
import { literalValues } from "@shared/validation/literal-values";

export const ORDER_STAGE = {
  DRAFT: "draft",
  PENDING_APPROVAL: "pending_approval",
  AWAITING_PICKUP: "awaiting_pickup",
  IN_TRANSIT: "in_transit",
  DELIVERED: "delivered",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
  REFUNDED: "refunded",
} as const satisfies Record<string, OrderStage>;

export const ORDER_STAGES = literalValues(ORDER_STAGE);

const LEGACY_ORDER_STAGE_ALIASES: Readonly<Record<string, OrderStage>> = {
  pending: ORDER_STAGE.AWAITING_PICKUP,
  confirmed: ORDER_STAGE.AWAITING_PICKUP,
  in_fulfillment: ORDER_STAGE.IN_TRANSIT,
};

export function normalizeOrderStage(value: string): OrderStage | undefined {
  return (
    ORDER_STAGES.find((stage) => stage === value) ??
    LEGACY_ORDER_STAGE_ALIASES[value]
  );
}

export const ORDER_STAGE_SEQUENCE = [
  ORDER_STAGE.DRAFT,
  ORDER_STAGE.PENDING_APPROVAL,
  ORDER_STAGE.AWAITING_PICKUP,
  ORDER_STAGE.IN_TRANSIT,
  ORDER_STAGE.DELIVERED,
  ORDER_STAGE.COMPLETED,
] as const;

export const TERMINAL_STAGES: ReadonlySet<OrderStage> = new Set([
  ORDER_STAGE.COMPLETED,
  ORDER_STAGE.CANCELLED,
  ORDER_STAGE.REFUNDED,
]);

export const INTERNAL_ONLY_STAGES: ReadonlySet<OrderStage> = new Set([
  ORDER_STAGE.DRAFT,
  ORDER_STAGE.PENDING_APPROVAL,
]);

export const OPEN_ORDER_STAGES = [
  ORDER_STAGE.AWAITING_PICKUP,
  ORDER_STAGE.IN_TRANSIT,
  ORDER_STAGE.DELIVERED,
] as const;

export const BOOKED_REVENUE_STAGES: ReadonlySet<string> = new Set<string>(
  ORDER_STAGES.filter(
    (stage) =>
      !INTERNAL_ONLY_STAGES.has(stage) &&
      stage !== ORDER_STAGE.CANCELLED &&
      stage !== ORDER_STAGE.REFUNDED,
  ),
);

export const DELIVERED_STAGES: ReadonlySet<OrderStage> = new Set([
  ORDER_STAGE.DELIVERED,
  ORDER_STAGE.COMPLETED,
]);

export const ORDER_AWAITING_FULFILLMENT_STAGES = [
  ORDER_STAGE.AWAITING_PICKUP,
] as const;

export const PRE_PICKUP_STAGES: ReadonlySet<OrderStage> = new Set([
  ORDER_STAGE.PENDING_APPROVAL,
  ORDER_STAGE.AWAITING_PICKUP,
]);

export const ORDER_ACTIVE_STAGES = [
  ORDER_STAGE.PENDING_APPROVAL,
  ORDER_STAGE.AWAITING_PICKUP,
  ORDER_STAGE.IN_TRANSIT,
  ORDER_STAGE.DELIVERED,
] as const;

export const QUICKBOOKS_PURCHASE_ORDER_STAGES = [
  ORDER_STAGE.AWAITING_PICKUP,
  ORDER_STAGE.IN_TRANSIT,
  ORDER_STAGE.DELIVERED,
] as const;

export function isQuickBooksPurchaseOrderStage(stage: string): boolean {
  return QUICKBOOKS_PURCHASE_ORDER_STAGES.some((candidate) => candidate === stage);
}

export const QUICKBOOKS_COUNTERPARTY_STAGES = [
  ...QUICKBOOKS_PURCHASE_ORDER_STAGES,
  ORDER_STAGE.COMPLETED,
] as const;

export const COUNTERPARTY_ACTIVE_ORDER_STAGES = ORDER_ACTIVE_STAGES.filter(
  (stage) => !INTERNAL_ONLY_STAGES.has(stage),
);

export const COUNTERPARTY_VISIBLE_ORDER_STAGES = ORDER_STAGES.filter(
  (stage) => !INTERNAL_ONLY_STAGES.has(stage),
);

export const ORDER_PAYMENT_STATUS = {
  PENDING_REVIEW: "pending_review",
  UNPAID: "unpaid",
  AWAITING_APPROVAL: "awaiting_approval",
  INVOICED: "invoiced",
  PAID: "paid",
  REFUNDED: "refunded",
  FAILED: "failed",
  UNKNOWN: "unknown",
} as const satisfies Record<string, OrderPaymentStatus>;

export const ORDER_PAYMENT_STATUSES = literalValues(ORDER_PAYMENT_STATUS);
